/**
 * InvestorProgramsPanel
 * ---------------------
 * Marketing + subscription surface for the Investor tier. Lives on
 * `/loan/loan-type` next to `ModeToggle`. Shows the full catalog of
 * investor-only loan programs and either upsells a Stripe checkout
 * (free users) or lets active subscribers manage / cancel.
 *
 * Source of truth for status is `useGetSubscriptionStatusQuery` (same hook
 * used by `useInvestorAccess` and `SubscriptionManagement`), so the panel
 * stays in sync with paywall gates elsewhere in the app.
 */

import { useState } from 'react';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import { LoadingButton } from '@mui/lab';
import {
  alpha,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { USER_LOAN_PROGRAM } from '@/config/constants';
import {
  useCancelSubscriptionMutation,
  useGetSubscriptionStatusQuery,
} from '@/apis/extended/subscriptionApi';

interface InvestorProgramSpec {
  program: USER_LOAN_PROGRAM;
  name: string;
  tagline: string;
  description: string;
  bestFor: string;
}

const INVESTOR_PROGRAMS: InvestorProgramSpec[] = [
  {
    program: USER_LOAN_PROGRAM.DSCR,
    name: 'DSCR',
    tagline: 'Qualify on rental income — no personal DTI required.',
    description:
      'Debt-Service Coverage Ratio loans underwrite the property, not your W-2s. Built for single-family, 2–4 unit, and short-term rentals.',
    bestFor: 'Buy-and-hold rentals · Airbnb / STR portfolios',
  },
  {
    program: USER_LOAN_PROGRAM.FIX_AND_FLIP,
    name: 'Fix & Flip',
    tagline: 'Purchase + rehab capital in a single short-term facility.',
    description:
      '12–24 month interest-only loans covering acquisition and renovation draws, designed to be refinanced or paid off at sale.',
    bestFor: 'Value-add SFR · Heavy / light rehab · BRRRR strategy',
  },
  {
    program: USER_LOAN_PROGRAM.BRIDGE,
    name: 'Bridge',
    tagline: 'Close fast while permanent financing comes together.',
    description:
      'Short-term interest-only financing to bridge gaps between purchase and sale, refinance, or stabilization.',
    bestFor: 'Non-contingent offers · Auction purchases · Lease-up gaps',
  },
  {
    program: USER_LOAN_PROGRAM.COMMERCIAL,
    name: 'Commercial',
    tagline: 'Multi-family, mixed-use, and small-balance commercial.',
    description:
      'Tailored programs for 5+ unit apartments, retail, office, industrial, and self-storage. Recourse and non-recourse structures available.',
    bestFor: 'Apartments · Mixed-use · Owner-user commercial',
  },
];

export const InvestorProgramsPanel = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { data: subscriptionStatus, isLoading } =
    useGetSubscriptionStatusQuery();
  const [cancelSubscription, { isLoading: isCancelling }] =
    useCancelSubscriptionMutation();
  const [isProcessing, setIsProcessing] = useState(false);

  const isInvestor = subscriptionStatus?.userType === 'investor';
  const isAdmin = subscriptionStatus?.userType === 'admin';
  const isActive = subscriptionStatus?.status === 'active';
  const hasAccess =
    isAdmin ||
    !!subscriptionStatus?.investorAccessGranted ||
    (isInvestor && isActive);

  const handleUpgrade = () => navigate('/pricing');

  const handleCancel = async () => {
    if (
      !window.confirm(
        'Cancel your investor subscription? You will retain access until the end of the current billing period.'
      )
    ) {
      return;
    }
    try {
      setIsProcessing(true);
      await cancelSubscription().unwrap();
      toast.success(
        'Subscription cancelled. Access will continue until the end of the billing period.'
      );
    } catch (e) {
      console.error('Cancel subscription failed', e);
      toast.error('Could not cancel subscription. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const statusChip = (() => {
    if (isAdmin) {
      return <Chip size="small" color="info" label="Admin · all access" />;
    }
    if (hasAccess) {
      return (
        <Chip
          size="small"
          color="success"
          icon={<CheckCircleOutlineIcon />}
          label={isActive ? 'Investor · active' : 'Investor · access granted'}
        />
      );
    }
    return <Chip size="small" label="Free plan" variant="outlined" />;
  })();

  const monthlyCost = subscriptionStatus?.subscription?.amount;
  const renewsOn = subscriptionStatus?.endDate
    ? new Date(subscriptionStatus.endDate).toLocaleDateString()
    : null;

  return (
    <Card
      sx={{
        background: `linear-gradient(135deg, ${alpha(
          theme.palette.success.main,
          0.06
        )} 0%, ${alpha(theme.palette.primary.main, 0.04)} 100%)`,
        border: `1px solid ${alpha(theme.palette.primary.main, 0.18)}`,
        borderRadius: 2,
        height: '100%',
      }}
    >
      <CardHeader
        avatar={<StarBorderIcon color="primary" />}
        title={
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={1}
          >
            <Typography variant="h6" fontWeight={700}>
              Investor programs
            </Typography>
            {statusChip}
          </Stack>
        }
        subheader={
          hasAccess
            ? 'Your account can quote DSCR, Bridge, Fix & Flip, and Commercial scenarios.'
            : 'Unlock investor-only loan programs and instant pre-quote scenarios.'
        }
        subheaderTypographyProps={{
          sx: { color: 'text.secondary', mt: 0.5 },
        }}
      />
      <CardContent sx={{ pt: 0 }}>
        {/* Subscription summary line for active members */}
        {hasAccess && !isAdmin && (monthlyCost || renewsOn) && (
          <Stack
            direction="row"
            gap={1}
            flexWrap="wrap"
            sx={{ mb: 2, color: 'text.secondary' }}
          >
            {monthlyCost && (
              <Typography variant="body2">
                <strong>${monthlyCost}</strong>/mo
              </Typography>
            )}
            {renewsOn && (
              <Typography variant="body2">
                · {isActive ? 'renews' : 'expires'} {renewsOn}
              </Typography>
            )}
          </Stack>
        )}

        {/* CTA */}
        {!hasAccess && (
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            sx={{ mb: 2 }}
          >
            <LoadingButton
              variant="contained"
              color="primary"
              loading={isLoading}
              onClick={handleUpgrade}
              fullWidth
            >
              Subscribe to investor access
            </LoadingButton>
            <Typography variant="caption" color="text.secondary">
              Stripe checkout · cancel anytime
            </Typography>
          </Stack>
        )}
        {hasAccess && !isAdmin && isActive && (
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            sx={{ mb: 2 }}
          >
            <Button
              variant="outlined"
              onClick={() => navigate('/pricing')}
              fullWidth
            >
              Manage plan
            </Button>
            <LoadingButton
              variant="text"
              color="error"
              loading={isCancelling || isProcessing}
              onClick={handleCancel}
              fullWidth
            >
              Cancel subscription
            </LoadingButton>
          </Stack>
        )}

        <Divider sx={{ my: 1.5 }} />

        <Typography
          variant="overline"
          color="text.secondary"
          sx={{ letterSpacing: 1, fontWeight: 700 }}
        >
          What you unlock
        </Typography>

        <List dense disablePadding sx={{ mt: 1 }}>
          {INVESTOR_PROGRAMS.map((p) => (
            <ListItem
              key={p.program}
              alignItems="flex-start"
              sx={{
                py: 1.25,
                px: 1.25,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1.5,
                mb: 1,
                background: theme.palette.background.paper,
              }}
            >
              <ListItemIcon sx={{ minWidth: 36, mt: 0.5 }}>
                {hasAccess ? (
                  <CheckCircleOutlineIcon color="success" fontSize="small" />
                ) : (
                  <LockOutlinedIcon color="action" fontSize="small" />
                )}
              </ListItemIcon>
              <ListItemText
                primary={
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="baseline"
                    flexWrap="wrap"
                  >
                    <Typography
                      variant="subtitle2"
                      fontWeight={700}
                      color="text.primary"
                    >
                      {p.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {p.tagline}
                    </Typography>
                  </Stack>
                }
                secondary={
                  <Box sx={{ mt: 0.5 }}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 0.5 }}
                    >
                      {p.description}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.disabled"
                      sx={{ fontStyle: 'italic' }}
                    >
                      Best for: {p.bestFor}
                    </Typography>
                  </Box>
                }
                secondaryTypographyProps={{ component: 'div' }}
              />
            </ListItem>
          ))}
        </List>

        {!hasAccess && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mt: 1 }}
          >
            Free plans keep access to Conventional, FHA, VA, and USDA. Investor
            subscription adds the four programs above plus instant scenario
            quoting.
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

export default InvestorProgramsPanel;
